import { Navigate, Route, Routes } from 'react-router'
import { useAuth } from './providers/auth/useAuth'
import { ChatProvider } from './providers/chat/ChatProvider'
import AppLayout from './components/layout/AppLayout'
import Billing from './pages/Billing'
import BrandBook from './pages/BrandBook'
import CompetitorResearch from './pages/CompetitorResearch'
import Files from './pages/Files'
import ForgotPassword from './pages/ForgotPassword'
import Conversations from './pages/Conversations'
import Home from './pages/Home'
import Integrations from './pages/Integrations'
import Login from './pages/Login'
import MyAds from './pages/MyAds'
import Permissions from './pages/Permissions'
import Plans from './pages/Plans'
import ResetPassword from './pages/ResetPassword'
import Signup from './pages/Signup'
import VerifyEmail from './pages/VerifyEmail'

const requiresVerifiedEmail = import.meta.env.VITE_APP_ENV !== 'local'

export default function App() {
  const { user, account, brand, loading } = useAuth()

  if (loading) return null

  const setupDone = Boolean(user && account)
  // The backend refuses to resolve an account for an unverified caller, so the
  // app would render and then fail every request. Ask first instead.
  //
  // Gated on the account existing, not just the user: creating the Firebase user
  // is the first half of signing up and it fires onAuthStateChanged immediately,
  // so gating earlier would unmount the signup form before it could call
  // /accounts/setup — the one route that deliberately stays open.
  if (setupDone && requiresVerifiedEmail && user && !user.emailVerified) return <VerifyEmail />

  return (
    <Routes>
      <Route
        element={
          setupDone ? (
            <ChatProvider key={brand?.id}>
              <AppLayout />
            </ChatProvider>
          ) : (
            <Navigate to={user ? '/signup' : '/login'} replace />
          )
        }
      >
        <Route index element={<Home />} />
        <Route path="c/:threadId" element={<Home />} />
        <Route path="my-ads" element={<MyAds />} />
        <Route path="competitor-research" element={<CompetitorResearch />} />
        <Route path="conversations" element={<Conversations />} />
        <Route path="brand-book" element={<BrandBook />} />
        <Route path="files" element={<Files />} />
        <Route path="permissions" element={<Permissions />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="plans" element={<Plans />} />
        <Route path="billing" element={<Billing />} />
        <Route path="billing/success" element={<Billing />} />
        <Route path="billing/cancel" element={<Billing />} />
      </Route>
      <Route
        path="/login"
        element={user ? <Navigate to={setupDone ? '/' : '/signup'} replace /> : <Login />}
      />
      <Route path="/signup" element={setupDone ? <Navigate to="/" replace /> : <Signup />} />
      <Route
        path="/forgot-password"
        element={user ? <Navigate to={setupDone ? '/' : '/signup'} replace /> : <ForgotPassword />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
